// Public-facing quality rating bucket (design-documents/039 §2) — shared by SourceCard,
// FindingsCard, and VerificationTracePanel so the three surfaces can never disagree on where
// a cross_design_quality score lands. The cut-offs are defined next to the study type weights
// they derive from, in @shared/design_scoring_profiles. Label only — no exact-score tooltip
// (deferred by Product; see design-documents/025).

import { rateCrossDesignQuality, type QualityRating } from "@shared/design_scoring_profiles";

export type QualityRatingBucket = QualityRating;

/**
 * Buckets a cross_design_quality score ([0, 1]) into a rating bucket.
 *
 * Returns a slug rather than a display string — public pages (SourceCard, FindingsCard) go
 * through i18n via QUALITY_RATING_LABEL_KEYS; internal-only surfaces (VerificationTracePanel)
 * can read QUALITY_RATING_ENGLISH_LABEL directly.
 */
export function qualityRatingBucket(crossDesignQuality: number): QualityRatingBucket {
  return rateCrossDesignQuality(crossDesignQuality);
}

/** i18n key (under the "sourceCard" namespace) for each bucket — for public, translated pages.
 *  Typed via `as const` (not `Record<QualityRatingBucket, string>`) so indexing this with a
 *  QualityRatingBucket keeps the literal key type i18next's typed `t()` requires. */
export const QUALITY_RATING_LABEL_KEYS = {
  gold: "sourceCard.qualityGold",
  high: "sourceCard.qualityHigh",
  moderate: "sourceCard.qualityModerate",
  low: "sourceCard.qualityLow",
} as const;

/** Hardcoded English label per bucket — for internal (#/internal/) surfaces, which don't use i18n. */
export const QUALITY_RATING_ENGLISH_LABEL: Record<QualityRatingBucket, string> = {
  gold: "Gold standard",
  high: "High quality",
  moderate: "Moderate quality",
  low: "Low quality",
};
